import moment from 'moment'
import Chart from 'chart.js'
import 'chartjs-adapter-moment'
import objectPath from 'object-path'

Chart.defaults.LineWithLine = Chart.defaults.line;
Chart.controllers.LineWithLine = Chart.controllers.line.extend({
    draw: function(ease) {
        Chart.controllers.line.prototype.draw.call(this, ease);

        if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
            const activePoint = this.chart.tooltip._active[0]
            const position = activePoint.tooltipPosition()
            const ctx = this.chart.ctx
            const topY = this.chart.legend.bottom
            const bottomY = this.chart.chartArea.bottom

            // draw line
            ctx.save()
            ctx.beginPath()
            ctx.moveTo(position.x, topY)
            ctx.lineTo(position.x, bottomY)
            ctx.lineWidth = 0.5
            ctx.strokeStyle = '#4EBEA9'
            ctx.stroke()
            ctx.restore()

            ctx.beginPath()
            ctx.strokeWidth = 0.5
            ctx.strokeStyle = '#4EBEA9'
            ctx.fillStyle = '#FFFFFF'
            ctx.arc(position.x, position.y, 2, 0, 2 * Math.PI, true)
            ctx.closePath()
            ctx.stroke()
            ctx.fill()
        }
    }
})

export default class PriceHistory {

    /**
     *
     * @param {HTMLCanvasElement} ctx
     * @param {Array.<Point>} data
     * @param {Object|PriceHistoryParams} params
     */
    constructor(ctx, data = [], params = {}) {
        this.params = PriceHistory._ensureParams(params)
        this.points = PriceHistory._createPoints(data, this.params.period)
        this.rangeDates = PriceHistory._findRangeDates(this.points.smooth)
        moment.locale(this.params.language)

        this.chart = new Chart(ctx, {
            type: 'LineWithLine',
            data: {
                labels: PriceHistory._createLabelsFromPoints([].concat(this.points.dash, this.points.smooth)),
                datasets: [
                    {
                        data: this.points.smooth,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderColor: '#969696',
                        lineTension: 0,
                        borderWidth: 2,
                        fill: false,
                    },
                    {
                        data: this.points.dash,
                        borderDash: [5, 5],
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderColor: '#969696',
                        lineTension: 0,
                        borderWidth: 1,
                        fill: false,
                    }
                ]
            },
            options: {
                animation: {
                    duration: 200,
                    easing: 'easeOutQuart'
                },
                responsive: false,
                legend: {
                    display: false,
                },
                title: {
                    display: false,
                },
                tooltips: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false,
                    enabled: false,
                    custom: function(tooltipModel) {
                        // Tooltip Element
                        let tooltipEl = document.getElementById('price-history-tooltip')

                        // Create element on first render
                        if (!tooltipEl) {
                            tooltipEl = document.createElement('div')
                            tooltipEl.id = 'price-history-tooltip'
                            tooltipEl.style.backgroundColor = '#FFFFFF'
                            tooltipEl.style.boxShadow = '0px 1px 2px rgba(0, 0, 0, 0.1), 0px 8px 16px rgba(0, 0, 0, 0.1)'
                            tooltipEl.style.borderRadius = '4px'
                            tooltipEl.style.position = 'absolute'
                            tooltipEl.style.padding = '4px 16px 10px 16px'
                            tooltipEl.style.pointerEvents = 'none'
                            tooltipEl.style.display = 'none'
                            tooltipEl.classList.add('pht-container')

                            document.body.appendChild(tooltipEl)
                            window.addEventListener('orientationchange', () => {
                                tooltipEl.style.opacity = 0;
                                tooltipEl.style.display = 'none'
                            });
                        }

                        // Hide if no tooltip
                        if (tooltipModel.opacity === 0) {
                            tooltipEl.style.opacity = 0;
                            tooltipEl.style.display = 'none'
                            return
                        }

                        if (tooltipModel.body) {
                            const titleLines = tooltipModel.title || [];
                            const bodyLines = tooltipModel.body.map((bodyItem) => bodyItem.lines);
                            const footerLines = tooltipModel.footer || [];

                            const arr = []
                            for (const title of titleLines) {
                                if (!title) {
                                    continue
                                }
                                const el = document.createElement('div')
                                el.classList.add('pht-container_title')
                                el.style.color = '#757575'
                                el.style.fontSize = '12px'
                                el.style.fontWeight = 'normal'
                                el.style.marginTop = '6px'
                                el.innerText = title
                                arr.push(el.outerHTML)
                            }

                            for (const bodyArr of bodyLines) {
                                for (const body of bodyArr) {
                                    if (!body) {
                                        continue
                                    }
                                    const el = document.createElement('div')
                                    el.classList.add('pht-container_body')
                                    el.style.color = '#333333'
                                    el.style.fontSize = '16px'
                                    el.style.fontWeight = 'bold'
                                    el.style.marginTop = '6px'
                                    el.innerText = body
                                    arr.push(el.outerHTML)
                                }
                            }

                            for (const footer of footerLines) {
                                if (!footer) {
                                    continue
                                }
                                const el = document.createElement('div')
                                el.classList.add('pht-container_footer')
                                el.style.color = '#333333'
                                el.style.fontSize = '12px'
                                el.style.fontWeight = 'normal'
                                el.style.marginTop = '6px'
                                el.innerText = footer
                                arr.push(el.outerHTML)
                            }

                            tooltipEl.innerHTML = arr.join('')
                        }

                        const rect = this._chart.canvas.getBoundingClientRect();
                        tooltipEl.style.opacity = 1
                        tooltipEl.style.display = 'unset'


                        const marginX = rect.left + window.pageXOffset + tooltipModel.caretX

                        if (marginX <= rect.width / 2) {
                            tooltipEl.style.left = (rect.left + window.pageXOffset + tooltipModel.caretX + 16) + 'px'
                        } else {
                            tooltipEl.style.left = (rect.left + window.pageXOffset + tooltipModel.caretX - 16 - tooltipEl.clientWidth) + 'px'
                        }

                        tooltipEl.style.top = (rect.top + window.pageYOffset + tooltipModel.caretY - 16) + 'px'
                    },
                    callbacks: {
                        title: (tooltipItems, options) => {
                            const index = tooltipItems[0]['index']
                            const datasetIndex = tooltipItems[0]['datasetIndex']
                            const date = options.datasets[datasetIndex]['data'][index]['x']
                            return moment(date).locale(this.params.language).format('D MMMM')
                        },
                        label: (tooltipItem, options) => {
                            if (tooltipItem.datasetIndex === 1) {
                                return undefined
                            }
                            const index = tooltipItem['index']
                            const value = options.datasets[0]['data'][index]['y']
                            return value + ' ' + this.params.currencySign
                        },
                        footer: (tooltipItems) => {
                            if (tooltipItems[0]['datasetIndex'] === 1) {
                                return PriceHistory._translations(this.params.language, 'NOT_PRICE')
                            }
                            return undefined
                        }
                    }
                },
                scales: {
                    xAxes: [
                        {
                            gridLines: {
                                display: true,
                                drawBorder: true,
                                drawOnChartArea: false,
                                tickMarkLength: 8
                            },
                            type: 'time',
                            distribution: 'linear',
                            time: {
                                displayFormats: {
                                    quarter: 'MMM'
                                },
                            },
                            ticks: {
                                source: 'labels',
                                callback: (dataLabel, index, items) => {
                                    if (index % 2) {
                                        const month = moment(items[index].value).format('MMM').replace('.', '')
                                        return month.charAt(0).toUpperCase() + month.slice(1)
                                    }
                                    return ''
                                },
                            },
                        },
                    ],
                    yAxes: [
                        {
                            type: 'time',
                            position: 'right',
                            gridLines: {
                                drawBorder: false,
                                drawOnChartArea: true,
                            },
                            ticks: {
                                source: 'data',
                                max: this.rangeDates.max,  // максимальное значение в графике
                                min: this.rangeDates.min,  // минимальное значение в графике
                                major: {
                                    enabled: true,
                                    fontStyle: 'bold'
                                },

                                callback: (dataLabel, index, ticks) => {
                                    return ticks[index]['value'] + ' ' + this.params.currencySign
                                },
                            },
                            afterBuildTicks: (scale, ticks) => {
                                if (!ticks) {
                                    return ticks
                                }
                                const distinct = []
                                return ticks.filter((item) => {
                                    if (distinct.includes(item.value)) {
                                        return false
                                    }

                                    distinct.push(item.value)
                                    if (this.rangeDates.last === item.value) {
                                        item.major = true
                                    }
                                    // Отрисовать линии только для
                                    // max value, min value, last value
                                    return [this.rangeDates.min, this.rangeDates.max, this.rangeDates.last].includes(item.value)
                                })
                            }
                        },
                    ],
                }
            }
        })
    }

    /**
     *
     * @param {Array.<Point>} data
     * @param {Object|PriceHistoryParams} params
     * @returns {PriceHistory}
     */
    update(data, params = {}) {
        this.params = PriceHistory._ensureParams(params, this.params)
        this.points = PriceHistory._createPoints(data, this.params.period)
        this.rangeDates = PriceHistory._findRangeDates(this.points.smooth)

        this.chart.data.datasets[0].data = this.points.smooth
        this.chart.data.datasets[1].data = this.points.dash
        this.chart.data.labels = PriceHistory._createLabelsFromPoints([].concat(this.points.dash, this.points.smooth))
        this.chart.options.scales.yAxes[0].ticks.max = this.rangeDates.max
        this.chart.options.scales.yAxes[0].ticks.min = this.rangeDates.min
        moment.locale(this.params.language)
        this.chart.update()
        return this
    }

    /**
     *
     * @param {Array.<Point>} points
     * @returns {Array.<Date>}
     * @private
     */
    static _createLabelsFromPoints(points) {
        const tmp = {}
        let prevMonth = null
        for (let i = 0; i < points.length; i++) {
            const item = points[i]
            const date = moment(item.x)
            const month = date.format('M');
            if (!tmp[month]) {
                tmp[month] = { first: item.x, last: item.x }
                if (prevMonth) {
                    tmp[month]['first'] = date.startOf('month').toDate()
                    tmp[prevMonth]['last'] = moment(tmp[prevMonth]['first']).endOf('month').toDate()
                }
            }
            tmp[month]['last'] = item.x
            prevMonth = month
        }

        const labels = []
        const months = Object.keys(tmp)
        for (let i = 0; i < months.length; i++) {
            const property = months[i]
            const firstDayOfMonth = moment(tmp[property]['first'])
            const lastDayOfMonth = moment(tmp[property]['last'])
            const days = Math.floor(lastDayOfMonth.diff(firstDayOfMonth, 'days') / 2)
            const middleOfMonth = firstDayOfMonth.add(days, 'days').toDate()
            labels.push(tmp[property]['first'])
            labels.push(middleOfMonth)
            if (i === months.length - 1) {
                labels.push(tmp[property]['last'])
            }
        }
        return labels;
    }

    /**
     *
     * @param {Array.<Point>} points
     * @returns {{min: number, last: number|undefined, max: number}}
     * @private
     */
    static _findRangeDates(points) {
        const options = {
            min: Infinity,
            max: -Infinity,
            last: undefined,
        }
        for (let i = 0; i < points.length; i++) {
            const item = points[i]
            options.last = item.y

            if (item.y < options.min) {
                options.min = item.y
            }
            if (item.y > options.max) {
                options.max = item.y
            }
        }
        return options
    }

    /**
     * @typedef {Object} Point
     * @property {(Date|string)} x
     * @property {Number} y
     */

    /**
     *
     * @param {Array.<Point>} data
     * @param {number} period
     * @returns {{dash: Array.<Point>, smooth: Array.<Point>}}
     * @private
     */
    static _createPoints(data, period) {
        const dash = []
        const smooth = []
        if (data.length === 0) {
            return { dash, smooth }
        }

        let prevDate = null
        let dashPrevDate = moment(data[data.length - 1].x).add(- period, 'months')
        let minDate = moment(data[data.length - 1].x).add(- period, 'months')
        for (const item of data) {
            // Пропустить точки у которых дата меньше минимальной даты
            if (moment(item.x).isBefore(minDate)) {
                continue
            }

            if (!prevDate) {
                // Посторение точек пунктирной линии. Период когда небыло цены.
                const days = Math.floor(moment(item.x).diff(dashPrevDate, 'days'))
                for (let i = 0; i < days; i++) {
                    dashPrevDate = moment(dashPrevDate).add(1, 'days').toDate()
                    dash.push({ x: dashPrevDate, y: Number(item.y) })
                }

                prevDate = moment(item.x).toDate()
            }
            // Посторение точек сплошной линии.
            const days = Math.floor(moment(item.x).diff(prevDate, 'days'))
            for (let i = 0; i <= days; i++) {
                smooth.push({ x: prevDate, y: Number(item.y) })
                prevDate = moment(prevDate).add(1, 'days').toDate()
            }
        }

        return { dash, smooth }
    }

    /**
     *
     * @param {string} language
     * @param {string} key
     * @param {Object} [params]
     * @returns {string|null}
     * @private
     */
    static _translations(language, key, params) {
        const translations = {
            ru: {
                NOT_PRICE: `В этот день Alitools \nне получал информацию \nо цене.`,
            },
            en: {
                NOT_PRICE: `Alitools didn’t receive \nany price info \non this day.`,
            }
        }
        let str = objectPath.get(translations, [language, key]) || objectPath.get(translations, ['en', key], '')
        for (const property in params) {
            str = String(str).replace(`{${property}}`, params[property])
        }
        return str
    }

    /**
     * @typedef {Object} PriceHistoryParams
     * @property {String|null} [currencySign] - Default is ''.
     * @property {String|null} [language] - Default is 'en'.
     * @property {Number|null} [period] - Max count month. Default is 3.
     */

    /**
     *
     * @param {(Object|PriceHistoryParams)} options
     * @param {Object} defaultOptions
     * @returns {{period: number, currencySign: string, language: string}}
     * @private
     */
    static _ensureParams(options, defaultOptions = {}) {
        if (!options) {
            options = {}
        }
        return {
            currencySign: objectPath.get(options, ['currencySign'], defaultOptions['currencySign'] || ''),
            language: objectPath.get(options, ['language'], defaultOptions['language'] || 'en'),
            period: objectPath.get(options, ['period'], defaultOptions['period'] || 3)
        }
    }
}